import { DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { merge } from "rxjs";
import { Headcount } from "../../../../../shared/bootstrap/app-config";
import { Flight } from "../../../../models/flight.model";
import { flag } from "../../../form/controls";
import { FlightGroup } from "../../../form/flight-form-group";
import { CrewArray } from "../crew-members-group/crew-members-group";

export function passengersControls(flight: Flight) {
    return {
        passengers: new FormControl<number | null>(flight.passengers, Validators.min(0)),
        payingPassengers: new FormControl<number | null>(flight.payingPassengers, Validators.min(0)),
        isTax: flag(flight.isTax),
    };
}

/**
 * The rules of the app for the people on board, which need the crew list and the pilots:
 * - when the pilots or the crew members change, the number follows them and keeps the extra people the user
 *   added (the number minus the previous count, never below zero);
 * - a number the user types below the count is an error, and stays as typed;
 * - the paying passengers never exceed the number.
 * They run on what the user changes, never on the values the dialog opens with.
 */
export function watchHeadcount(group: FlightGroup, crews: CrewArray, headcount: Headcount, destroyRef: DestroyRef): void {
    const { passengers, payingPassengers, pilotId, coPilot } = group.controls;

    const aboard = () => {
        const crew = crews.length;
        if (headcount !== "pob") return crew;
        return (pilotId.value !== null ? 1 : 0) + (coPilot.value ? 1 : 0) + crew;
    };

    const minimum: ValidatorFn = (control): ValidationErrors | null => {
        const value = control.value as number | null;
        if (headcount === "none" || value === null || !control.dirty) return null;
        const min = aboard();
        return value < min ? { [headcount === "pob" ? "minPob" : "minPax"]: min } : null;
    };
    passengers.addValidators(minimum);

    const maximum: ValidatorFn = (control): ValidationErrors | null => {
        const value = control.value as number | null;
        const max = passengers.value ?? 0;
        // Only what the user touches is checked, so a flight logged with other numbers opens valid.
        return value !== null && (control.dirty || passengers.dirty) && value > max ? { maxPax: max } : null;
    };
    payingPassengers.addValidators(maximum);

    let last = aboard();
    if (headcount !== "none") {
        merge(crews.valueChanges, pilotId.valueChanges, coPilot.valueChanges)
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => {
                const total = aboard();
                if (total === last) return;
                const extra = passengers.value === null ? 0 : Math.max(0, passengers.value - last);
                last = total;
                passengers.setValue(total + extra > 0 ? total + extra : null);
            });
    }
    passengers.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => payingPassengers.updateValueAndValidity({ emitEvent: false }));
    passengers.updateValueAndValidity({ emitEvent: false });
}
